// Location resolution for NEWLY CREATED encounters.
//
// THE BUG THIS EXISTS TO PREVENT
// ------------------------------
// The ambient `location` from useAuth is a VIEW FILTER, not a creation default.
// In a multi-location organization the header renders an explicit "All" option
// whose value is the empty string (Header.js), so an empty ambient location is a
// perfectly valid thing for a clinician to be *looking at* — and a completely
// invalid thing for a new encounter to *carry*: the encounter list queries are
// location-scoped (useChats.js), so an encounter created with locationId "" is
// invisible in every filtered view and lands nowhere for billing.
//
// Nine separate call sites create encounters, and most of them simply read the
// filter (`location || ""`) as if it were the creation default. Single-location
// orgs never noticed, because useAuth force-assigns the only location. Orgs with
// several locations hit it constantly.
//
// Rather than trust nine call sites — and every future one — to tell the filter
// apart from the creation default, resolution lives here, and api.createChat
// stamps the resolved value as a last line of defence.
//
// RESOLUTION ORDER
// ----------------
//   1. an explicit candidate (a form selection, or the source encounter of a
//      follow-up) when it is a location the user actually has
//   2. the current view filter, when it is scoped to a single real location
//   3. the user's durable home location (see getHomeLocationId)
//   4. the only location, when the user has exactly one
//   5. unresolved -> the caller must ask; nothing is invented
//
// Note what is deliberately NOT here: falling back to "the first location in the
// list". Guessing a site produces a plausible-looking encounter filed against
// the wrong facility, which is worse for billing than an obviously empty one.

const idOf = (loc) => (loc && loc.id != null ? String(loc.id) : "");

export const getUserLocations = (user) => (Array.isArray(user?.locations) ? user.locations : []);

/** True when the org uses locations at all. Orgs without them are left alone. */
export const usesLocations = (user) => getUserLocations(user).length > 0;

export const isValidLocationId = (user, locationId) => {
  if (locationId === null || locationId === undefined || locationId === "") return false;
  return getUserLocations(user).some((loc) => idOf(loc) === String(locationId));
};

// The user's durable "home" location: where their encounters belong when the
// view filter can't say. `homeLocationId` is written by changeLocation whenever
// a real location is picked, so switching the filter to "All" no longer erases
// the preference — which is exactly what used to happen, since changeLocation
// persists its argument straight into currentLocationId/actualLocationId and
// the "All" option's argument is "".
export const getHomeLocationId = (user) => {
  const candidates = [user?.custom?.homeLocationId, user?.custom?.actualLocationId, user?.currentLocationId];
  const found = candidates.find((id) => isValidLocationId(user, id));
  return found === undefined ? null : String(found);
};

/**
 * True when we must ask the user to choose a home location before they can
 * create anything. Only multi-location users can be in this state: with exactly
 * one location there is nothing to ask about, and with none the org doesn't use
 * locations.
 */
export const needsHomeLocation = (user) => getUserLocations(user).length > 1 && !getHomeLocationId(user);

/**
 * The location a new encounter should carry, or null when it cannot be
 * determined without asking the user.
 */
export const resolveCreationLocationId = ({ user, filterLocation, candidate } = {}) => {
  const locations = getUserLocations(user);
  if (locations.length === 0) return null;

  if (isValidLocationId(user, candidate)) return String(candidate);
  if (isValidLocationId(user, filterLocation)) return String(filterLocation);

  const home = getHomeLocationId(user);
  if (home) return home;

  if (locations.length === 1) return idOf(locations[0]);

  return null;
};

/** Thrown by the api-layer backstop when an encounter would be created without a location. */
export class MissingEncounterLocationError extends Error {
  constructor(message = "Encounter cannot be created without a location") {
    super(message);
    this.name = "MissingEncounterLocationError";
    this.code = "MISSING_ENCOUNTER_LOCATION";
  }
}

export const isMissingLocationError = (err) => err?.code === "MISSING_ENCOUNTER_LOCATION";

// Context variable stamped on encounters that had to be created without a
// resolvable location (offline replay only — see stampCreationLocation).
export const UNRESOLVED_LOCATION_FLAG = "local:locationUnresolved";

// --- Ambient context registry -------------------------------------------------
//
// api.js is a plain module with no access to React context, but it is the one
// place every creation path funnels through. useAuth publishes the current user
// and view filter here so the backstop can resolve without them.

let context = { user: null, filterLocation: undefined };

export const setEncounterLocationContext = (next) => {
  context = { user: next?.user ?? null, filterLocation: next?.filterLocation };
};

export const getEncounterLocationContext = () => context;

/** Test seam. */
export const resetEncounterLocationContext = () => {
  context = { user: null, filterLocation: undefined };
};

/**
 * Backstop applied inside api.createChat — the guarantee that no encounter
 * reaches the backend without a location.
 *
 * A caller that already resolved a valid location keeps it untouched. A caller
 * that passed the view filter (or nothing at all) gets the resolved value
 * substituted. When nothing resolves, this throws rather than creating an
 * orphaned encounter — except for `allowUnresolved`, which exists solely for
 * offline replay: there is no user present to ask, and losing a recorded
 * clinical encounter is worse than filing one that needs its location fixed.
 */
export const stampCreationLocation = (chat, { allowUnresolved = false } = {}) => {
  const { user, filterLocation } = context;
  if (!usesLocations(user)) return chat;

  const resolved = resolveCreationLocationId({ user, filterLocation, candidate: chat?.locationId });
  if (resolved) return { ...chat, locationId: resolved };

  if (!allowUnresolved) throw new MissingEncounterLocationError();

  return {
    ...chat,
    locationId: chat?.locationId || "",
    contextVariables: { ...(chat?.contextVariables || {}), [UNRESOLVED_LOCATION_FLAG]: "true" },
  };
};
